import React, { useCallback, useEffect, useState } from 'react';
import { KeyRound, Loader2, Printer, Trash2, Users } from 'lucide-react';
import { api } from '../context/AuthContext';

const ROLLE = { owner: 'Inhaber (alles)', employee: 'Mitarbeiter (lesen & hochladen)' };
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString('de-DE') : '');

const briefLaden = async (code) => {
  const r = await api.post('/konto/codes/brief', { code }, { responseType: 'blob' });
  const url = URL.createObjectURL(r.data);
  const a = document.createElement('a'); a.href = url; a.download = `Verknuepfungscode_${code}.pdf`; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
};

const NeuerCode = ({ mnr, email, name, onDone, onError }) => {
  const [rolle, setRolle] = useState('owner');
  const [mail, setMail] = useState(email || '');
  const [busy, setBusy] = useState(false);
  const [neu, setNeu] = useState(null);
  const erzeugen = async () => {
    setBusy(true);
    try { const { data } = await api.post('/konto/codes', { mandanten_nr: mnr, role: rolle, email: mail, name, senden: !!mail }); setNeu(data); onDone(); }
    catch (e) { onError(e?.response?.data?.detail || 'Code konnte nicht erzeugt werden'); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs">
      <select value={rolle} onChange={(e) => setRolle(e.target.value)} className="border border-gray-200 px-2 py-1 bg-white" data-testid="code-rolle">
        <option value="owner" label={ROLLE.owner} /><option value="employee" label={ROLLE.employee} />
      </select>
      <input type="email" value={mail} onChange={(e) => setMail(e.target.value)} placeholder="E-Mail für Versand (optional)" className="border border-gray-200 px-2 py-1 w-56" data-testid="code-email" />
      <button type="button" onClick={erzeugen} disabled={busy} className="inline-flex items-center gap-1 px-3 py-1 bg-[#1B3A5C] text-white rounded-sm disabled:opacity-50" data-testid="code-erzeugen">
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <KeyRound className="w-3 h-3" />} Code erzeugen
      </button>
      {neu && (
        <span className="flex items-center gap-2 bg-amber-50 border border-amber-200 px-2 py-1" data-testid="code-neu">
          <span className="font-mono font-semibold tracking-widest text-[#1B3A5C]" data-testid="code-neu-wert">{neu.code}</span>
          <span className="text-[#7a8a99]">{neu.mail === 'gesendet' ? 'per Mail gesendet' : neu.mail === 'fehlgeschlagen' ? 'Mail fehlgeschlagen' : 'nicht versendet'} · gültig bis {fmt(neu.expires_at)}</span>
          <button type="button" onClick={() => briefLaden(neu.code)} className="inline-flex items-center gap-1 text-[#b8893d] hover:underline" data-testid="code-brief"><Printer className="w-3 h-3" />Brief (PDF)</button>
        </span>
      )}
    </div>
  );
};

export const ZugaengeCodes = ({ mnr, email, name, onError }) => {
  const [d, setD] = useState(null);
  const laden = useCallback(() => api.get(`/konto/zugaenge/${mnr}`).then((r) => setD(r.data)).catch(() => setD({ zugaenge: [], offene_codes: [] })), [mnr]);
  useEffect(() => { laden(); }, [laden]);
  const entziehen = async (id) => { if (window.confirm('Zugriff dieses Web-Kontos auf die Mandantennummer entziehen?')) { await api.delete(`/konto/zugaenge/${id}`); laden(); } };
  const widerrufen = async (id) => { await api.delete(`/konto/codes/${id}`); laden(); };
  if (!d) return null;
  return (
    <div className="border-t border-gray-100 px-4 py-3 space-y-2" data-testid="zugaenge-codes">
      <p className="text-[10px] tracking-wider text-[#7a8a99] flex items-center gap-1"><Users className="w-3 h-3" />WEB-KONTEN MIT ZUGRIFF AUF M {mnr}</p>
      {d.zugaenge.length === 0 && <p className="text-xs text-[#7a8a99]">Noch kein Web-Konto verknüpft.</p>}
      {d.zugaenge.map((z) => (
        <div key={z.id} className="flex items-center gap-3 text-xs" data-testid={`zugang-${z.id}`}>
          <span className="text-[#1B3A5C] font-medium">{z.name || z.email}</span><span className="text-[#7a8a99]">{z.email}</span>
          <span className="px-1.5 py-0.5 bg-[#f5f8fa] text-[#34536b]">{ROLLE[z.role]}</span><span className="text-[#7a8a99]">via {z.via}</span>
          <button type="button" onClick={() => entziehen(z.id)} className="ml-auto text-rose-600 hover:text-rose-800" title="Zugriff entziehen" data-testid={`zugang-entziehen-${z.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
      ))}
      {d.offene_codes.map((c) => (
        <div key={c.id} className="flex items-center gap-3 text-xs text-[#7a8a99]" data-testid={`code-offen-${c.id}`}>
          <KeyRound className="w-3 h-3" /> Offener Code ({ROLLE[c.role]}){c.email ? ` an ${c.email}` : ''} · gültig bis {fmt(c.expires_at)}
          <button type="button" onClick={() => widerrufen(c.id)} className="ml-auto text-rose-600 hover:underline" data-testid={`code-widerrufen-${c.id}`}>widerrufen</button>
        </div>
      ))}
      <NeuerCode mnr={mnr} email={email} name={name} onDone={laden} onError={onError} />
    </div>
  );
};
